import React, { useEffect, useState } from "react";
import { isEmpleado, isGestorFiscal, clearToken, getToken } from "../services/auth";

function decodeUserFromToken() {
  const t = getToken();
  if (!t) return null;
  try {
    const [, payload] = t.split(".");
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(atob(b64).split("").map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join(""));
    const p = JSON.parse(json);
    return p.username || p.sub || p.name || null;
  } catch { return null; }
}

function logout() {
  try { clearToken(); } catch {}
  window.location.replace("#/login");
  setTimeout(() => { try { window.location.reload(); } catch {} }, 50);
}

// ── Iconos SVG inline (estilo Lucide, monocolor stroke) ─────────────
const Icons = {
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  euro: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 7A6 6 0 1 0 18 17" />
      <path d="M5 11h10" />
      <path d="M5 14h10" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </svg>
  ),
  invoice: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  ),
  briefcase: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  ),
  scale: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="M3 12l2-5 2 5a3 3 0 0 1-4 0z" />
      <path d="M17 12l2-5 2 5a3 3 0 0 1-4 0z" />
      <path d="M8 21h8" />
    </svg>
  ),
  truck: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 3h15v13H1z" />
      <path d="M16 8h4l3 3v5h-7z" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
};

// ── Paleta de tintas por módulo (claro + medio) ─────────────────────
// Cada modulo tiene una "tinta" propia, suave (no chillón) para diferenciar visualmente
const TINTAS = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    border: "hover:border-blue-400",    bar: "bg-blue-500"    },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "hover:border-amber-400",   bar: "bg-amber-500"   },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "hover:border-emerald-400", bar: "bg-emerald-500" },
  sky:     { bg: "bg-sky-50",     text: "text-sky-700",     border: "hover:border-sky-400",     bar: "bg-sky-500"     },
  violet:  { bg: "bg-violet-50",  text: "text-violet-700",  border: "hover:border-violet-400",  bar: "bg-violet-500"  },
  teal:    { bg: "bg-teal-50",    text: "text-teal-700",    border: "hover:border-teal-400",    bar: "bg-teal-500"    },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    border: "hover:border-rose-400",    bar: "bg-rose-500"    },
  cyan:    { bg: "bg-cyan-50",    text: "text-cyan-700",    border: "hover:border-cyan-400",    bar: "bg-cyan-500"    },
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-700",  border: "hover:border-indigo-400",  bar: "bg-indigo-500"  },
};

const fmtEur = (n) =>
  Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
const fmtInt = (n) => Number(n || 0).toLocaleString("es-ES");

function Kpi({ label, value, sub, trend, accent = "blue" }) {
  const t = TINTAS[accent];
  const trendColor = trend > 0 ? "text-emerald-600" : trend < 0 ? "text-rose-600" : "text-slate-400";
  const trendArrow = trend > 0 ? "↑" : trend < 0 ? "↓" : "·";
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-shadow hover:shadow-md">
      <div className={`h-1.5 ${t.bar}`}></div>
      <div className="px-6 py-5">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">{label}</div>
        <div className="text-3xl font-semibold text-slate-900 font-mono tabular-nums">{value}</div>
        <div className="flex items-center gap-2 mt-2 text-sm">
          {trend !== undefined && trend !== null && (
            <span className={`font-medium ${trendColor}`}>
              {trendArrow} {Math.abs(trend).toFixed(1)}%
            </span>
          )}
          <span className="text-slate-500">{sub}</span>
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ href, external, icon, titulo, sub, badge, accent = "blue" }) {
  const t = TINTAS[accent];
  const target = external ? { rel: "noopener noreferrer" } : {};
  return (
    <a
      href={href}
      {...target}
      className={`group block bg-white border border-slate-200 rounded-xl p-6 transition-all duration-150 ${t.border} hover:shadow-md hover:-translate-y-0.5 relative`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-14 h-14 rounded-xl ${t.bg} ${t.text} flex items-center justify-center transition-transform group-hover:scale-110`}>
          <div className="w-7 h-7">{icon}</div>
        </div>
        {badge !== undefined && badge !== null && badge > 0 && (
          <span className="text-xs font-semibold bg-rose-100 text-rose-700 px-2.5 py-1 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-5">
        <div className="text-lg font-semibold text-slate-900">{titulo}</div>
        <div className="text-sm text-slate-500 mt-1 leading-relaxed">{sub}</div>
      </div>
    </a>
  );
}

export default function Dashboard({ username: usernameProp, onLogout: onLogoutProp } = {}) {
  const username = usernameProp || decodeUserFromToken();
  const onLogout = onLogoutProp || logout;
  const [kpis, setKpis] = useState({ fact: null, nom: null, sol: null, soc: null });
  const [branding, setBranding] = useState({ nombre: "ASOTRANS S.COOP.", nif: "F09664814" });
  const [reboot, setReboot] = useState(null);
  const [enviandoReboot, setEnviandoReboot] = useState(false);

  useEffect(() => {
    const safeFetch = (url) =>
      fetch(url, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    safeFetch("/facturacion/api/kpis").then((d) => setKpis((k) => ({ ...k, fact: d })));
    safeFetch("/nominas/api/kpis").then((d) => setKpis((k) => ({ ...k, nom: d })));
    safeFetch("/solicitudes/api/_kpis").then((d) => setKpis((k) => ({ ...k, sol: d })));
    safeFetch("/socios-explorer/api/kpis").then((d) => setKpis((k) => ({ ...k, soc: d })));
    safeFetch("/branding.json").then((d) => { if (d && d.nombre) { setBranding(d); document.title = d.nombre; } });
  }, []);

  // Estado de reinicio del servidor (aviso de mantenimiento). Sondea cada 60s.
  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      const t = getToken(); // se relee cada tick por si el token cambio
      return fetch("/api/sistema/reboot-estado", { headers: t ? { Authorization: `Bearer ${t}` } : {} })
        .then((r) => (r.ok ? r.json() : null))
        // si falla (401/expiro/red) NO borramos un aviso ya mostrado: conservamos el ultimo estado
        .then((d) => { if (vivo && d) setReboot(d); })
        .catch(() => {});
    };
    cargar();
    const iv = setInterval(cargar, 60000);
    return () => { vivo = false; clearInterval(iv); };
  }, []);

  const confirmarReboot = () => {
    if (enviandoReboot) return;
    if (!window.confirm("Esto reiniciará el servidor esta noche a las 03:00, tras una copia de seguridad. ¿Continuar?")) return;
    setEnviandoReboot(true);
    const t = getToken();
    fetch("/api/sistema/reboot-confirmar", { method: "POST", headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then((r) => r.json())
      .then((d) => {
        if (d && d.ok) setReboot((rb) => ({ ...(rb || {}), confirmado: true, confirmado_en: d.confirmado_en }));
        else alert((d && d.error) || "No se pudo confirmar el reinicio.");
      })
      .catch(() => alert("Error de conexión al confirmar el reinicio."))
      .finally(() => setEnviandoReboot(false));
  };
  const cancelarReboot = () => {
    if (enviandoReboot) return;
    setEnviandoReboot(true);
    const t = getToken();
    fetch("/api/sistema/reboot-cancelar", { method: "POST", headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then((r) => r.json())
      .then((d) => {
        if (d && d.ok) setReboot((rb) => ({ ...(rb || {}), confirmado: false, confirmado_en: null }));
        else alert((d && d.error) || "No se pudo cancelar el reinicio. Inténtalo de nuevo.");
      })
      .catch(() => alert("Error de conexión al cancelar el reinicio."))
      .finally(() => setEnviandoReboot(false));
  };

  const rol = isEmpleado() ? "empleado" : isGestorFiscal() ? "gestor_fiscal" : "otro";
  const tema = branding.tema && branding.tema.c1 ? branding.tema : { c1: "#2563eb", c2: "#06b6d4", logo: "" };

  const cards = [
    { key: "socios", titulo: "Socios", icon: Icons.users, accent: "blue",
      sub: kpis.soc ? `${kpis.soc.activos} activos · ${kpis.soc.total} totales` : "Explorador de socios",
      badge: kpis.soc?.con_avisos,
      href: "/socios-explorer/", external: true, roles: ["empleado"] },
    { key: "tareas", titulo: "Tareas", icon: Icons.clipboard, accent: "amber",
      sub: kpis.sol ? `${kpis.sol.total_abiertas} abiertas · ${kpis.sol.sin_asignar} sin asignar` : "Correos y solicitudes",
      badge: kpis.sol?.total_abiertas,
      href: "/solicitudes/", external: true, roles: ["empleado"] },
    { key: "liquidaciones", titulo: "Liquidaciones", icon: Icons.euro, accent: "emerald",
      sub: "Ingresos y gastos por socio",
      href: "/liquidacion/", external: true, roles: ["empleado"] },
    { key: "informes", titulo: "Informes", icon: Icons.chart, accent: "sky",
      sub: "Desglose de gastos y cuotas",
      href: "/liquidacion/informes", external: true, roles: ["empleado"] },
    { key: "facturacion", titulo: "Facturación", icon: Icons.invoice, accent: "violet",
      sub: kpis.fact ? `${fmtEur(kpis.fact.facturado_mes)} este mes` : "Facturas, clientes y remesas",
      href: "/facturacion/", external: true, roles: ["empleado"] },
    { key: "nominas", titulo: "Nóminas", icon: Icons.briefcase, accent: "teal",
      sub: kpis.nom ? `${kpis.nom.activas_total} en alta · ${kpis.nom.confirmadas_mes} confirmadas` : "Sueldos por chofer",
      badge: kpis.nom?.pendientes_mes,
      href: "/nominas/", external: true, roles: ["empleado"] },
    { key: "fiscal", titulo: "Fiscal", icon: Icons.scale, accent: "rose",
      sub: "Modelos AEAT, IVA, IRPF",
      href: "/fiscal/", external: true, roles: ["empleado", "gestor_fiscal"] },
    { key: "flota", titulo: "Flota", icon: Icons.truck, accent: "cyan",
      sub: "GPS y tacógrafo en tiempo real",
      href: "/flota/", external: true, roles: ["empleado"] },
    { key: "portal-socios", titulo: "Portal Socios", icon: Icons.users, accent: "indigo",
      sub: "Gestión del portal del socio",
      href: "/socio/admin/sso", external: true, roles: ["empleado"] },
  ].filter((c) => c.roles.includes(rol));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 font-sans">
      {/* Header con franja superior azul corporativo */}
      <div className="h-1.5" style={{ background: `linear-gradient(to right, ${tema.c1} 0%, ${tema.c1} 55%, ${tema.c2} 100%)` }}></div>
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {tema.logo ? (
              <img src={tema.logo} alt={branding.nombre} className="h-12 w-auto" />
            ) : (
              <div className="w-11 h-11 rounded-xl text-white text-base font-bold flex items-center justify-center shadow-sm" style={{ background: `linear-gradient(135deg, ${tema.c1}, ${tema.c2})` }}>
                {(branding.nombre || "A").charAt(0)}
              </div>
            )}
            <div>
              <div className="text-lg font-semibold text-slate-900 tracking-tight leading-tight">{branding.nombre}</div>
              <div className="text-sm text-slate-500 leading-tight">Panel principal</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {username && (
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-slate-50 border border-slate-200">
                <div className="w-9 h-9 rounded-full text-white text-sm font-semibold flex items-center justify-center shadow-sm" style={{ background: `linear-gradient(135deg, ${tema.c1}, ${tema.c2})` }}>
                  {username.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:inline text-base font-medium text-slate-700">{username}</span>
              </div>
            )}
            <button
              onClick={onLogout}
              className="text-sm font-medium text-slate-500 hover:text-rose-600 transition px-4 py-2 rounded-lg hover:bg-rose-50"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-8 lg:px-12 py-10">
        {/* Aviso de mantenimiento: reinicio pendiente del servidor */}
        {reboot && reboot.reboot_pendiente && (
          <div className={`mb-8 rounded-xl border px-6 py-5 flex items-start gap-4 ${reboot.confirmado ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
            <div className={`mt-0.5 flex-shrink-0 ${reboot.confirmado ? "text-emerald-600" : "text-amber-600"}`}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                {reboot.confirmado
                  ? <><path d="M12 22a10 10 0 1 0-10-10" /><polyline points="8 12 11 15 16 9" /></>
                  : <><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0z" /></>}
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">
                {reboot.confirmado ? `Reinicio confirmado para esta noche a las ${reboot.hora_reinicio || "03:00"}` : "Actualización pendiente: requiere reiniciar el servidor"}
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {reboot.confirmado
                  ? <>Se hará una copia de seguridad y, <strong>solo si la copia se completa correctamente</strong>, el servidor se reiniciará. Si la copia falla, el reinicio se pospone. {reboot.confirmado_en && <span className="text-slate-400">(confirmado: {reboot.confirmado_en})</span>}</>
                  : `Se ha instalado una actualización que necesita reiniciar. Si lo confirmas, se reiniciará esta noche a las ${reboot.hora_reinicio || "03:00"} tras una copia de seguridad automática.`}
                {reboot.paquetes && reboot.paquetes.length > 0 && (
                  <span className="block text-xs text-slate-400 mt-1">Paquetes: {reboot.paquetes.join(", ")}</span>
                )}
                {reboot.ultimo_intento && /(ABORTADO|CADUCADO)/.test(reboot.ultimo_intento) && (
                  <span className="block mt-2 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1">
                    ⚠ El último intento no se completó: {reboot.ultimo_intento.replace(/^\S+\s+/, "")}
                  </span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              {reboot.confirmado
                ? <button onClick={cancelarReboot} disabled={enviandoReboot} className="text-sm font-medium text-slate-600 hover:text-rose-600 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:border-rose-200 transition disabled:opacity-50">{enviandoReboot ? "…" : "Cancelar"}</button>
                : <button onClick={confirmarReboot} disabled={enviandoReboot} className="text-sm font-semibold text-white px-5 py-2.5 rounded-lg shadow-sm hover:shadow transition disabled:opacity-60" style={{ background: `linear-gradient(135deg, ${tema.c1}, ${tema.c2})` }}>{enviandoReboot ? "Enviando…" : `Confirmar reinicio (${reboot.hora_reinicio || "03:00"})`}</button>}
            </div>
          </div>
        )}
        {/* KPIs */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <span className="inline-block w-1.5 h-4 rounded-sm" style={{ background: `linear-gradient(to bottom, ${tema.c1}, ${tema.c2})` }}></span>
              Indicadores
            </h2>
            <span className="text-sm text-slate-400">en tiempo real</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <Kpi
              label="Facturado este mes"
              value={kpis.fact ? fmtEur(kpis.fact.facturado_mes) : "—"}
              trend={kpis.fact?.diff_pct}
              sub="vs mes anterior"
              accent="violet"
            />
            <Kpi
              label="Socios activos"
              value={kpis.soc ? fmtInt(kpis.soc.activos) : "—"}
              sub={kpis.soc ? `de ${fmtInt(kpis.soc.total)} totales` : ""}
              accent="blue"
            />
            <Kpi
              label="Tareas abiertas"
              value={kpis.sol ? fmtInt(kpis.sol.total_abiertas) : "—"}
              sub={kpis.sol ? `${kpis.sol.sin_asignar} sin asignar` : ""}
              accent="amber"
            />
            <Kpi
              label="Pendiente de cobro"
              value={kpis.fact ? fmtEur(kpis.fact.sin_cobrar_total) : "—"}
              sub={kpis.fact ? `${kpis.fact.n_sin_cobrar} facturas` : ""}
              accent="rose"
            />
          </div>
        </section>

        {/* Modulos */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <span className="inline-block w-1.5 h-4 rounded-sm" style={{ background: `linear-gradient(to bottom, ${tema.c1}, ${tema.c2})` }}></span>
              Módulos
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {cards.map((c) => (
              <ModuleCard key={c.key} {...c} />
            ))}
          </div>
        </section>
      </main>

      <footer className="max-w-[1400px] mx-auto px-8 lg:px-12 pb-8 mt-16">
        <div className="text-sm text-slate-400 text-center">
          {branding.nombre} · CIF {branding.nif} · {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}
